/* CenCal $30 OFF popup + trigger pill. State machine, Klaviyo single opt-in. */
(function () {
  'use strict';

  // ----- config -----
  const STATE_KEY = 'cc_state';                  // 'fresh' | 'dismissed' | 'subscribed'
  const COOKIE_DISMISS = 'cc_popup_dismissed';   // fallback for incognito
  const COOKIE_SUBSCRIBED = 'cc_subscribed';
  const COOKIE_DAYS_DISMISS = 30;
  const COOKIE_DAYS_SUBSCRIBED = 365;
  const TIMER_MS = 8000;
  const KLAVIYO_BASE = 'https://a.klaviyo.com/client/subscriptions';
  const KLAVIYO_REV = '2026-04-15';
  const OPEN_DELAY = 30;
  const CLOSE_MS = 380;
  const TRIGGER_SHOW_DELAY = 400; // after close, before pill slides in

  const root = document.getElementById('cc-popup-root');
  if (!root) return;

  const COMPANY = root.dataset.klaviyoCompany;
  const SMS_LIST = root.dataset.smsList;
  const EMAIL_LIST = root.dataset.emailList;

  const overlay = root.querySelector('[data-cc-overlay]');
  const triggerPill = root.querySelector('[data-cc-trigger]');

  // ----- storage helpers -----
  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie = name + '=' + value + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }
  function getCookie(name) {
    return document.cookie.split('; ').reduce((r, v) => {
      const p = v.split('=');
      return p[0] === name ? decodeURIComponent(p[1]) : r;
    }, '');
  }
  function getState() {
    let s = null;
    try { s = localStorage.getItem(STATE_KEY); } catch (_) {}
    if (!s) {
      if (getCookie(COOKIE_SUBSCRIBED)) s = 'subscribed';
      else if (getCookie(COOKIE_DISMISS)) s = 'dismissed';
      else s = 'fresh';
    }
    return s;
  }
  function setState(s) {
    try { localStorage.setItem(STATE_KEY, s); } catch (_) {}
    if (s === 'dismissed') setCookie(COOKIE_DISMISS, '1', COOKIE_DAYS_DISMISS);
    if (s === 'subscribed') {
      setCookie(COOKIE_SUBSCRIBED, '1', COOKIE_DAYS_SUBSCRIBED);
      setCookie(COOKIE_DISMISS, '1', COOKIE_DAYS_SUBSCRIBED);
    }
  }

  // ----- popup open/close -----
  let opened = false;
  let phoneCaptured = null;

  function open() {
    if (opened) return;
    opened = true;
    hideTrigger();
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.add('cc-open');
      });
    });
    track('popup_opened');
  }

  function close(markDismissed) {
    if (!opened) return;
    root.classList.remove('cc-open');
    root.classList.add('cc-closing');
    setTimeout(() => {
      overlay.hidden = true;
      root.classList.remove('cc-closing');
      document.body.style.overflow = '';
      opened = false;
      // If user closed without subscribing, set dismissed state + show trigger
      if (markDismissed !== false && getState() !== 'subscribed') {
        setState('dismissed');
        setTimeout(showTrigger, TRIGGER_SHOW_DELAY);
      }
    }, CLOSE_MS);
  }

  function showStep(name, opts) {
    opts = opts || {};
    const current = root.querySelector('.cc-step:not([hidden])');
    const next = root.querySelector('.cc-step[data-step="' + name + '"]');
    if (!next) return;
    if (current === next) return;
    if (current) {
      current.hidden = true;
      current.style.animation = 'none';
      void current.offsetWidth;
      current.style.animation = '';
    }
    next.hidden = false;
    next.style.animation = 'none';
    void next.offsetWidth;
    next.style.animation = '';

    if (name === 'success' && !opts.skipConfetti) launchConfetti();
  }

  // ----- trigger pill -----
  function showTrigger() {
    if (!triggerPill) return;
    if (getState() === 'subscribed') return;
    // Don't show on cart/checkout (CSS body class also handles this)
    const path = location.pathname || '';
    if (/^\/cart/.test(path) || /^\/checkouts?\//.test(path)) return;
    triggerPill.hidden = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        triggerPill.classList.add('cc-trigger-visible');
      });
    });
  }
  function hideTrigger() {
    if (!triggerPill) return;
    triggerPill.classList.remove('cc-trigger-visible');
    setTimeout(() => {
      if (!triggerPill.classList.contains('cc-trigger-visible')) {
        triggerPill.hidden = true;
      }
    }, 350);
  }

  // ----- validation -----
  function normalizePhone(raw) {
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
    return null;
  }
  function validEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
  }

  function maskPhone(value) {
    const d = value.replace(/\D/g, '').slice(0, 10);
    if (d.length === 0) return '';
    if (d.length <= 3) return '(' + d;
    if (d.length <= 6) return '(' + d.slice(0,3) + ') ' + d.slice(3);
    return '(' + d.slice(0,3) + ') ' + d.slice(3,6) + '-' + d.slice(6);
  }

  // ----- Klaviyo -----
  // CRITICAL: Use XMLHttpRequest, NOT fetch.
  // Shopify's Web Pixels Manager (shop_events_listener.js) wraps window.fetch
  // and the wrapper triggers an OPTIONS preflight that Klaviyo's CORS config rejects
  // (datadome-protected, allow-methods only lists OPTIONS).
  // XMLHttpRequest is not wrapped by WPM and works directly.
  function klaviyoSubscribe(payload) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', KLAVIYO_BASE + '/?company_id=' + COMPANY, true);
      xhr.setRequestHeader('Content-Type', 'application/vnd.api+json');
      xhr.setRequestHeader('revision', KLAVIYO_REV);
      xhr.onload = function () {
        if (xhr.status === 200 || xhr.status === 202) {
          resolve(true);
        } else {
          reject(new Error('klaviyo ' + xhr.status + ': ' + (xhr.responseText || '').slice(0, 200)));
        }
      };
      xhr.onerror = function () {
        reject(new Error('klaviyo network error'));
      };
      xhr.send(JSON.stringify(payload));
    });
  }
  function buildPhonePayload(phoneE164) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'cc-popup-welcome30',
          profile: { data: { type: 'profile', attributes: {
            phone_number: phoneE164,
            subscriptions: {
              sms: {
                marketing: { consent: 'SUBSCRIBED' },
                transactional: { consent: 'SUBSCRIBED' }
              }
            }
          } } }
        },
        relationships: { list: { data: { type: 'list', id: SMS_LIST } } }
      }
    };
  }
  function buildEmailPayload(email, phoneE164) {
    const attrs = {
      email: email,
      subscriptions: {
        email: { marketing: { consent: 'SUBSCRIBED' } }
      }
    };
    if (phoneE164) attrs.phone_number = phoneE164;
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'cc-popup-welcome30',
          profile: { data: { type: 'profile', attributes: attrs } }
        },
        relationships: { list: { data: { type: 'list', id: EMAIL_LIST } } }
      }
    };
  }

  function setBusy(form, busy) {
    const btn = form.querySelector('[data-cc-submit]');
    const lbl = btn.querySelector('.cc-btn-label');
    const spn = btn.querySelector('.cc-btn-spinner');
    btn.disabled = busy;
    if (lbl) lbl.style.opacity = busy ? '0.55' : '1';
    if (spn) spn.hidden = !busy;
  }
  function showError(form, msg) {
    const el = form.querySelector('[data-cc-error]');
    if (msg) {
      el.textContent = msg;
      el.hidden = false;
      el.style.animation = 'none';
      void el.offsetWidth;
      el.style.animation = '';
    } else {
      el.hidden = true;
    }
  }

  async function handlePhone(form) {
    const raw = form.querySelector('[data-cc-input="phone"]').value;
    const e164 = normalizePhone(raw);
    if (!e164) { showError(form, 'enter a valid 10-digit US number.'); return; }
    showError(form, '');
    setBusy(form, true);
    try {
      await klaviyoSubscribe(buildPhonePayload(e164));
      phoneCaptured = e164;
      track('phone_submitted');
      showStep('email');
    } catch (err) {
      console.error('[cc-popup] phone submit failed', err);
      showError(form, 'something went wrong. try again?');
    } finally {
      setBusy(form, false);
    }
  }
  async function handleEmail(form) {
    const email = form.querySelector('[data-cc-input="email"]').value.trim();
    if (!validEmail(email)) { showError(form, 'enter a valid email.'); return; }
    showError(form, '');
    setBusy(form, true);
    try {
      await klaviyoSubscribe(buildEmailPayload(email, phoneCaptured));
      track('email_submitted');
      setState('subscribed');
      showStep('success');
    } catch (err) {
      console.error('[cc-popup] email submit failed', err);
      showError(form, 'something went wrong. try again?');
    } finally {
      setBusy(form, false);
    }
  }

  function track(event) {
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', 'cc_popup_' + event]);
      }
    } catch (_) {}
  }

  // ----- confetti -----
  function launchConfetti() {
    const modal = root.querySelector('.cc-popup-modal');
    if (!modal) return;
    let layer = modal.querySelector('.cc-confetti');
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'cc-confetti';
      modal.appendChild(layer);
    }
    layer.innerHTML = '';
    const colors = ['#d4af37', '#e8c252', '#fff8dc', '#0a0a0a', '#ffffff'];
    const N = 18;
    for (let i = 0; i < N; i++) {
      const p = document.createElement('span');
      p.className = 'cc-confetti-piece';
      const x = Math.random() * 100;
      const delay = Math.random() * 0.25;
      const size = 6 + Math.random() * 6;
      p.style.left = x + '%';
      p.style.top = '-12px';
      p.style.width = size + 'px';
      p.style.height = (size * (0.6 + Math.random()*0.6)) + 'px';
      p.style.background = colors[i % colors.length];
      p.style.transform = 'rotate(' + (Math.random()*360) + 'deg)';
      p.style.animationDelay = delay + 's';
      p.style.animationDuration = (1.2 + Math.random() * 0.9) + 's';
      layer.appendChild(p);
    }
    setTimeout(() => { if (layer) layer.innerHTML = ''; }, 2400);
  }

  // ----- wire up -----
  root.querySelectorAll('[data-cc-close]').forEach(btn =>
    btn.addEventListener('click', () => close(true)));
  root.querySelectorAll('[data-cc-dismiss]').forEach(btn =>
    btn.addEventListener('click', e => { e.preventDefault(); close(true); }));
  overlay.addEventListener('click', e => {
    if (e.target === e.currentTarget) close(true);
  });
  const skipBtn = root.querySelector('[data-cc-skip]');
  if (skipBtn) skipBtn.addEventListener('click', () => {
    track('skip_to_email');
    phoneCaptured = null;
    showStep('email');
  });

  root.querySelectorAll('.cc-form').forEach(form => {
    form.addEventListener('submit', e => {
      e.preventDefault();
      if (form.dataset.ccForm === 'phone') handlePhone(form);
      else if (form.dataset.ccForm === 'email') handleEmail(form);
    });
  });

  const phoneInput = root.querySelector('[data-cc-input="phone"]');
  if (phoneInput) {
    phoneInput.addEventListener('input', () => {
      const before = phoneInput.value;
      const masked = maskPhone(before);
      if (masked !== before) phoneInput.value = masked;
    });
  }

  const copyBtn = root.querySelector('[data-cc-copy]');
  if (copyBtn) copyBtn.addEventListener('click', () => {
    const code = root.querySelector('[data-cc-code]').textContent.trim();
    const label = copyBtn.querySelector('.cc-copy-label');
    const done = () => {
      copyBtn.classList.add('copied');
      label.textContent = 'COPIED';
      setTimeout(() => { copyBtn.classList.remove('copied'); label.textContent = 'COPY'; }, 1800);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(() => {
        const ta = document.createElement('textarea');
        ta.value = code; document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); done(); } catch (_) {}
        document.body.removeChild(ta);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = code; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); done(); } catch (_) {}
      document.body.removeChild(ta);
    }
  });

  // Trigger pill click → reopen popup
  if (triggerPill) {
    triggerPill.addEventListener('click', () => {
      track('trigger_clicked');
      open();
    });
  }

  // Mobile drag-to-dismiss
  (function dragDismiss() {
    const modal = root.querySelector('.cc-popup-modal');
    let startY = null, curY = null;
    function start(e) { startY = (e.touches ? e.touches[0].clientY : e.clientY); curY = startY; modal.style.transition = 'none'; }
    function move(e) {
      if (startY === null) return;
      curY = e.touches ? e.touches[0].clientY : e.clientY;
      const dy = Math.max(0, curY - startY);
      modal.style.transform = 'translateY(' + dy + 'px)';
    }
    function end() {
      if (startY === null) return;
      const dy = curY - startY;
      modal.style.transition = '';
      modal.style.transform = '';
      if (dy > 120) close(true);
      startY = null; curY = null;
    }
    const drag = root.querySelector('[data-cc-drag]');
    if (!drag) return;
    drag.addEventListener('touchstart', start, { passive: true });
    drag.addEventListener('touchmove', move, { passive: true });
    drag.addEventListener('touchend', end);
  })();

  // ----- BOOTSTRAP based on state -----
  const state = getState();

  if (state === 'subscribed') {
    // never show anything
  } else if (state === 'dismissed') {
    // skip auto-open, show pill after a short settle
    setTimeout(showTrigger, 1500);
  } else {
    // fresh visitor: auto-open after 8s + exit intent
    setTimeout(() => { if (getState() === 'fresh') open(); }, TIMER_MS);
    document.addEventListener('mouseout', e => {
      if (!opened && e.clientY <= 0 && e.relatedTarget === null && getState() === 'fresh') open();
    });
  }

  // Escape always closes if open
  document.addEventListener('keydown', e => {
    if (opened && e.key === 'Escape') close(true);
  });
})();
